import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Edit3, BarChart3, Download, Code, Share2, FolderInput, Trash2 } from 'lucide-react';

interface TourMenuSnapshotProps {
  cardSelector: string;
  visible: boolean;
  stepRunId?: string;
  highlightItem?: string;
  centerInContent?: boolean;
  onRectReady?: (rect: { top: number; left: number; width: number; height: number } | null) => void;
}

const MENU_ITEMS = [
  { icon: Settings, get label() { return translateInterfaceText("Personalizar"); } },
  { icon: Edit3, get label() { return translateInterfaceText("Renomear"); } },
  { icon: BarChart3, get label() { return translateInterfaceText("Métricas"); } },
  { icon: Download, get label() { return translateInterfaceText("Download"); } },
  { icon: Code, get label() { return translateInterfaceText("Copiar código"); } },
  { icon: Share2, get label() { return translateInterfaceText("Compartilhar"); } },
];

const MENU_ITEMS_BOTTOM = [
  { icon: FolderInput, get label() { return translateInterfaceText("Mover para"); }, hasArrow: true },
];

const MENU_ITEM_DANGER = { icon: Trash2, get label() { return translateInterfaceText("Deletar"); } };

export function TourMenuSnapshot({ cardSelector, visible, stepRunId, highlightItem, centerInContent, onRectReady }: TourMenuSnapshotProps) {
  useLanguage();
  const [pos, setPos] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const runIdRef = useRef(stepRunId);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    runIdRef.current = stepRunId;
  }, [stepRunId]);

  const measureAndReport = useCallback(() => {
    if (!containerRef.current || !visible) {
      onRectReady?.(null);
      return;
    }
    const el = containerRef.current;
    const rect = el.getBoundingClientRect();
    const padding = 10;
    onRectReady?.({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [visible, onRectReady]);

  useEffect(() => {
    if (!visible) { setPos(null); onRectReady?.(null); return; }

    const calculate = () => {
      if (centerInContent) {
        const allMains = document.querySelectorAll('main');
        let contentEl: Element = document.body;
        allMains.forEach((m) => {
          if ((m as HTMLElement).offsetWidth > 0 && (m as HTMLElement).offsetHeight > 0) {
            contentEl = m;
          }
        });
        const contentRect = contentEl.getBoundingClientRect();
        const snapshotWidth = Math.min(520, contentRect.width * 0.52);
        const snapshotHeight = 340;
        setPos({
          top: contentRect.top + (contentRect.height / 2) - (snapshotHeight / 2),
          left: contentRect.left + (contentRect.width / 2) - (snapshotWidth / 2),
          width: snapshotWidth,
          height: snapshotHeight,
        });
      } else {
        const card = document.querySelector(cardSelector) as HTMLElement;
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const menuWidth = 192;
        setPos({
          top: rect.top + 36,
          left: rect.right - menuWidth - 4,
          width: menuWidth,
          height: 300,
        });
      }
    };

    calculate();
    const interval = setInterval(calculate, 500);
    return () => clearInterval(interval);
  }, [visible, cardSelector, centerInContent]);

  useEffect(() => {
    if (pos && visible) {
      const raf = requestAnimationFrame(() => {
        measureAndReport();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [pos, visible, measureAndReport]);

  if (!visible || !pos) return null;

  const snapshotWidth = centerInContent ? undefined : 192;

  return createPortal(
    <div
      ref={containerRef}
      data-tour="video-actions-snapshot"
      className="pointer-events-none"
      style={{
        position: 'fixed',
        zIndex: 10000,
        top: pos.top,
        left: pos.left,
        width: centerInContent ? 'min(520px, 52vw)' : snapshotWidth,
        maxWidth: centerInContent ? 560 : undefined,
        animation: 'tour-snapshot-in 0.25s ease-out',
      }}
    >
      {/* Dashed border */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: -10,
          borderRadius: 14,
          border: '2px dashed #4B7BFF',
          zIndex: 1,
        }}
      />
      <div
        className="rounded-lg border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden relative"
        style={{ fontSize: 14, zIndex: 2 }}
      >
        <div className="py-1">
          {MENU_ITEMS.map((item) => {
            const isHighlighted = highlightItem === item.label;
            return (
              <div
                key={item.label}
                data-tour={isHighlighted ? 'snapshot-highlight-item' : undefined}
                className={`flex items-center gap-2 px-3 py-[7px] text-sm transition-colors ${
                  isHighlighted
                    ? 'bg-[#4B7BFF] text-white rounded-sm mx-1'
                    : 'text-foreground'
                }`}
              >
                <item.icon className={`w-4 h-4 ${isHighlighted ? 'text-white' : 'text-muted-foreground'}`} />
                <span className={isHighlighted ? 'font-medium' : ''}>{item.label}</span>
              </div>
            );
          })}
        </div>
        <div className="h-px bg-border" />
        <div className="py-1">
          {MENU_ITEMS_BOTTOM.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between px-3 py-[7px] text-sm text-foreground"
            >
              <div className="flex items-center gap-2">
                <item.icon className="w-4 h-4 text-muted-foreground" />
                <span>{item.label}</span>
              </div>
              {item.hasArrow && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
            </div>
          ))}
        </div>
        <div className="h-px bg-border" />
        <div className="py-1">
          <div className="flex items-center gap-2 px-3 py-[7px] text-sm text-destructive">
            <MENU_ITEM_DANGER.icon className="w-4 h-4" />
            <span>{MENU_ITEM_DANGER.label}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
